/* site/pages/LandingPage.jsx — the home page, ported section for section from
 * lhc-site/lhc-landing-page.html (2026-09-07 redesign). The sections are direct
 * children of <main> exactly as in the prototype, because its stylesheet and its
 * section-snap both address `main > section`. Everything visible comes from the
 * editable content (content/landing.js defaults).
 *
 * Its stylesheet (home.css) is the prototype's own, scoped one class deeper
 * (.lhc-site.lhc-home) so this page's newer design cannot touch the other site
 * pages, which still run on site.css. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePageMeta } from "../SiteLayout";
import { useSiteContent } from "../content/SiteContent";
import LANDING from "../content/landing";
import { SiteLink } from "../siteLinks";
import { useConfirm } from "../SiteChrome";
import { onPlaceholderError } from "../placeholderImg";
import "./home.css";

/* ── a composition panel: quad / duo / trio share one markup shape ── */
function Panel({ base, item, className = "", eager = false }) {
  return (
    <SiteLink href={item.href} className={`${base}-item ${className}`.trim()}>
      <div className={`${base}-media`}>
        <img loading={eager ? "eager" : "lazy"} src={item.image} alt={item.alt || ""} onError={onPlaceholderError} />
      </div>
      <div className={`${base}-info`}>
        <span className="eyebrow">{item.eyebrow}</span>
        <h3 className={`${base}-name`}>{item.name}</h3>
        <p className={`${base}-meta`}>{item.meta}</p>
      </div>
    </SiteLink>
  );
}

/* ── full-screen slider: one frame every `interval` ms, a dot restarts the run ── */
function FsSlider({ id, name, set, interval }) {
  const slides = set.slides || [];
  const [cur, setCur] = useState(0);
  const [run, setRun] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setCur((v) => (v + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval, run]);
  const go = (i) => { setCur(i); setRun((v) => v + 1); };
  return (
    <section className="fs-slider" id={id} data-slider={name} aria-label={set.label} aria-roledescription="carousel">
      <div className="fs-slides">
        {slides.map((s, i) => (
          <figure className={"fs-slide" + (i === cur ? " is-active" : "")} key={s.image}>
            <img loading={i === 0 ? "eager" : "lazy"} src={s.image} alt={`Placeholder: ${set.eyebrow} imagery`} onError={onPlaceholderError} />
            <figcaption className="fs-slide-caption">
              <span className="eyebrow">{set.eyebrow}</span>
              <div className="t">{s.title}</div>
              <SiteLink href={set.link} className="fs-discover">{set.cta}</SiteLink>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="fs-slider-dots">
        {slides.map((s, i) => (
          <button type="button" key={s.image} aria-label={`Slide ${i + 1}`} className={i === cur ? "is-active" : undefined} data-i={i} onClick={() => go(i)} />
        ))}
      </div>
    </section>
  );
}

/* ── section snap (the prototype's wheel-driven one section per gesture).
   Desktop + fine pointer + motion-ok only; touch and trackpad-less input keep
   fully native scrolling. The intro band rests with its BOTTOM at the viewport
   bottom, so the film stays whole above it. ── */
function useSectionSnap() {
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    if (!window.matchMedia("(min-width:901px)").matches) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (!window.matchMedia("(pointer:fine)").matches) return undefined;
    const main = document.querySelector("main");
    if (!main) return undefined;

    let animating = false, check = null, safety = null;
    const points = () => {
      const pts = [];
      Array.prototype.forEach.call(main.children, (sec) => {
        if (sec.tagName !== "SECTION") return;
        const top = sec.getBoundingClientRect().top + window.scrollY;
        pts.push(sec.id === "s1b"
          ? Math.max(0, Math.round(top + sec.offsetHeight - window.innerHeight))
          : Math.max(0, Math.round(top)));
      });
      pts.push(document.documentElement.scrollHeight - window.innerHeight);
      return pts.sort((a, b) => a - b).filter((p, i, a) => i === 0 || p - a[i - 1] > 4);
    };
    const go = (target) => {
      animating = true;
      window.scrollTo({ top: target, behavior: "smooth" });
      let last = -1, stable = 0;
      clearInterval(check); clearTimeout(safety);
      check = setInterval(() => {
        const y = Math.round(window.scrollY);
        if (Math.abs(y - target) < 2 || y === last) stable++; else stable = 0;
        last = y;
        if (stable > 2) { clearInterval(check); animating = false; }
      }, 60);
      safety = setTimeout(() => { clearInterval(check); animating = false; }, 900);
    };
    const nextPoint = (y, down) => {
      const pts = points();
      if (down) { for (let i = 0; i < pts.length; i++) if (pts[i] > y + 2) return pts[i]; }
      else { for (let i = pts.length - 1; i >= 0; i--) if (pts[i] < y - 2) return pts[i]; }
      return null;
    };
    const onWheel = (e) => {
      if (document.body.hasAttribute("data-lhc-editing")) return;   // free scrolling while editing
      if (animating) { e.preventDefault(); return; }
      if (Math.abs(e.deltaY) < 4) return;
      const target = nextPoint(window.scrollY, e.deltaY > 0);
      if (target === null) return;                       // nothing that way — native scroll
      e.preventDefault();
      go(target);
    };
    const onKey = (e) => {
      if (animating) return;
      const down = e.key === "PageDown" || e.key === "ArrowDown" || (e.key === " " && !e.shiftKey);
      const up = e.key === "PageUp" || e.key === "ArrowUp" || (e.key === " " && e.shiftKey);
      if (!down && !up) return;
      /* never hijack keys while someone is typing — including a contenteditable, which is how the
         visual editor edits text in place (a typed SPACE was being swallowed as "scroll down") */
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""))) return;
      if (document.body.hasAttribute("data-lhc-editing")) return;
      const target = nextPoint(window.scrollY, down);
      if (target !== null) { e.preventDefault(); go(target); }
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      clearInterval(check); clearTimeout(safety);
    };
  }, []);
}

export default function LandingPage() {
  const c = useSiteContent("landing", LANDING);
  usePageMeta(c.meta);
  useSectionSnap();
  const [subscribed, subscribe] = useConfirm();
  const videoRef = useRef(null);
  /* no film source (or a source that fails) → the poster still, as the prototype does */
  const [noVideo, setNoVideo] = useState(!c.video.src);
  useEffect(() => { setNoVideo(!c.video.src); }, [c.video.src]);
  const onVideoError = useCallback(() => setNoVideo(true), []);

  return (
    <>
      {/* S1 — FULL-SCREEN FILM */}
      <section className={"fs-video" + (noVideo ? " no-video" : "")} id="s1" aria-label={c.video.label} data-testid="site-landing">
        <h1 className="visually-hidden">{c.video.heading}</h1>
        <video ref={videoRef} className="fs-video-media" id="homeVideo" autoPlay muted loop playsInline poster={c.video.poster} onError={onVideoError}>
          {c.video.src ? <source src={c.video.src} type={c.video.type} /> : null}
        </video>
        <div className="fs-video-fallback" aria-hidden="true">
          <img src={c.video.fallback} alt="" onError={onPlaceholderError} />
        </div>
        <a href="#s1b" className="scroll-cue" aria-label="Scroll down">
          <span className="cue-label">{c.video.scrollCue}</span>
          <span className="stem" />
          <svg className="cue-arrow" width="18" height="11" viewBox="0 0 18 11" fill="none" aria-hidden="true"><path d="M1 1l8 8 8-8" stroke="currentColor" strokeWidth="1.5" /></svg>
        </a>
      </section>

      {/* S1b — INTRO BAND */}
      <section className="intro-band" id="s1b" aria-label={c.introBand.label}>
        <div className="intro-band-inner reveal">
          <h2 className="intro-band-title">{c.introBand.title}</h2>
          <div className="intro-band-links">
            {c.introBand.links.map((l, i) => (
              <React.Fragment key={l.label}>
                {i > 0 ? <span className="intro-band-sep" /> : null}
                <SiteLink href={l.href}>{l.label}</SiteLink>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* S2 — THE FOUR PRODUCT WORLDS */}
      <section className="fs-quad" id="s2" aria-label={c.quad.label}>
        {c.quad.items.map((it) => <Panel key={it.name + it.eyebrow} base="quad" item={it} />)}
      </section>

      {/* S3 — NEWEST KITCHEN */}
      <FsSlider id="s3" name="kitchen" set={c.sliders.kitchen} interval={c.sliders.interval} />

      {/* S4 — NEWEST WARDROBE */}
      <section className="fs-duo" id="s4new" aria-label={c.duo.label}>
        {c.duo.items.map((it) => <Panel key={it.name} base="duo" item={it} />)}
      </section>

      {/* S5 — NEWEST SOFA */}
      <FsSlider id="s5new" name="sofa" set={c.sliders.sofa} interval={c.sliders.interval} />

      {/* S6 — NEWEST OUTDOOR */}
      <section className="fs-trio" id="s6new" aria-label={c.trio.label}>
        <Panel base="trio" item={c.trio.lead} className="trio-lead" />
        <div className="trio-stack">
          {c.trio.stack.map((it) => <Panel key={it.name} base="trio" item={it} />)}
        </div>
      </section>

      {/* PROFESSIONAL AREA */}
      <section className="section professional-section" id="professional">
        <div className="wrap professional-inner">
          <div className="reveal">
            <p className="eyebrow">{c.professional.eyebrow}</p>
            <h2 className="display-lg" style={{ marginTop: "var(--sp-3)" }}>{c.professional.title}</h2>
            <p className="body-lg" style={{ marginTop: "var(--sp-4)", maxWidth: "44ch" }}>{c.professional.body}</p>
            <div className="professional-ctas">
              <SiteLink href={c.professional.cta.href} className="btn btn--outline">{c.professional.cta.label}</SiteLink>
            </div>
          </div>
          <div className="professional-media reveal">
            <div className="media media--zoom">
              <img loading="lazy" src={c.professional.image} alt={c.professional.imageAlt || ""} onError={onPlaceholderError} />
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL + NEWSLETTER */}
      <section className="connect-section" id="connect" aria-label={c.connect.label}>
        <div className="connect-media" aria-hidden="true">
          <img loading="lazy" src={c.connect.image} alt="" onError={onPlaceholderError} />
        </div>
        <div className="connect-content">
          <div className="connect-lead reveal">
            <p className="eyebrow">{c.connect.eyebrow}</p>
            <h2 className="display-lg">{c.connect.title}</h2>
          </div>
          <div className="connect-actions">
            {subscribed ? (
              <p className="connect-confirm">{c.connect.confirm}</p>
            ) : (
              <form className="connect-form reveal" id="connectForm" onSubmit={subscribe}>
                <input type="email" name="email" placeholder={c.connect.placeholder} required aria-label={c.connect.placeholder} />
                <button type="submit" className="btn btn--on-dark">{c.connect.button}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
